import { useState, useEffect } from "react";
import styles from "./AllCommunityPage.module.css";
import { FiSearch } from "react-icons/fi";
import { ENDPOINTS } from "../../utils/endpoints";
import api from "../../services/api";
import useDashboardStore from "../../store/dashboardStore";
import useAuthStore from "../../store/authStore";
import chatStore from "../../store/chatStore";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import CommunityMembersPopup from "../../components/CommunityMembersPopup/CommunityMembersPopup";

const AllCommunities = () => {
  const {
    communities,
    setCommunities,
    setIsCreateCommunityScreen,
    setEditCommunityId,
    allChannels,
  } = useDashboardStore();
  const liveUserCounts = chatStore((state) => state.liveUserCounts);
  const { user } = useAuthStore();
  const isAdmin = user?.roles?.[0] === "administrator";
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  // { type: "members" | "traders", community } | null
  const [usersPopup, setUsersPopup] = useState(null);

  const handleEdit = (community) => {
    setEditCommunityId(community.id);
    setIsCreateCommunityScreen();
  };

  const handleDelete = (community) => {
    confirmAlert({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this community?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            const { data } = await api.delete(
              ENDPOINTS.DELETE_COMMUNITY_BY_ID(community.id)
            );
            if (data.success) {
              fetchCommunities();
              toast.success(data.message || "Community deleted successfully");
            }
          },
        },
        {
          label: "No",
          onClick: () => toast.error("Community deletion cancelled"),
        },
      ],
    });
  };

  const fetchCommunities = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get(
        `${ENDPOINTS.GET_ALL_COMMUNITIES}?validate=true`
      );
      if (data.success) {
        setCommunities(data.communities);
      }
    } catch (err) {
      console.error("Failed to fetch communities", err);
      toast.error("Failed to fetch communities");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const getCommunities = async () => {
      try {
        setIsLoading(true);
        const { data } = await api.get(
        `${ENDPOINTS.GET_ALL_COMMUNITIES}?validate=true`
      );
        if (data.success) {
          setCommunities(data.communities);
        }
      } catch (err) {
        console.error("Failed to fetch communities", err);
        toast.error("Failed to fetch communities");
      } finally {
        setIsLoading(false);
      }
    };
    getCommunities();
  }, [setCommunities]);

  const filteredCommunities = communities.filter((community) =>
    community.name.toLowerCase().includes(search.toLowerCase())
  );

  const groupedByCommunity = allChannels.reduce((acc, channel) => {
    const communityName = channel.community;
    const count = liveUserCounts[channel.id] || 0;

    if (!acc[communityName]) {
      acc[communityName] = 0;
    }

    acc[communityName] += count;
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.appBar}>
          <h2 className={styles.title}>Communities</h2>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Type in to search .."
              className={styles.searchInput}
              disabled
            />
            <FiSearch className={styles.searchIcon} />
          </div>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Loading communities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.appBar}>
        <h2 className={styles.title}>Communities</h2>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Type in to search .."
            className={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FiSearch className={styles.searchIcon} />
        </div>
      </div>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Community title</th>
              <th>Channels</th>
              <th>Traders</th>
              <th>Members</th>
              <th>theme</th>
              <th>Creation date</th>
              <th>Online</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredCommunities.map((comm, i) => (
              <tr key={i}>
                <td>{comm.name}</td>
                <td>{comm.channels_count}</td>
                <td>
                  {isAdmin ? (
                    <span
                      className={styles.countLink}
                      onClick={() =>
                        setUsersPopup({ type: "traders", community: comm })
                      }
                    >
                      {comm.traders_count}
                    </span>
                  ) : (
                    comm.traders_count
                  )}
                </td>
                <td>
                  {isAdmin ? (
                    <span
                      className={styles.countLink}
                      onClick={() =>
                        setUsersPopup({ type: "members", community: comm })
                      }
                    >
                      {comm.members_count}
                    </span>
                  ) : (
                    comm.members_count
                  )}
                </td>
                <td>
                  <div
                    className={styles.themeBox}
                    style={{ backgroundColor: comm.theme_color }}
                  />
                </td>
                <td>{dayjs(comm.created_at).format("DD-MM-YYYY")}</td>
                <td className={styles.online}>
                  {groupedByCommunity[comm.name] || 0}
                </td>
                <td>
                  <span
                    className={styles.edit}
                    onClick={() => handleEdit(comm)}
                  >
                    Edit
                  </span>
                  <span className={styles.divider}> - </span>
                  <span
                    className={styles.delete}
                    onClick={() => handleDelete(comm)}
                  >
                    Delete
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {usersPopup && (
        <CommunityMembersPopup
          scope="community"
          id={usersPopup.community.id}
          name={usersPopup.community.name}
          type={usersPopup.type}
          onClose={() => setUsersPopup(null)}
        />
      )}
    </div>
  );
};

export default AllCommunities;
